"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import { useChainArch, useCreateOfferFlow } from "@opensea/wallet"
import { useTranslations } from "next-intl"
import { useMakeOfferFragment } from "./use-make-offer.fragment"
import { isItemTradable } from "./validation/tradable"

export const useMakeOffer = (
  itemKeys:
    | (FragmentOf<typeof useMakeOfferFragment> & { quantity?: number })[]
    | undefined,
) => {
  const t = useTranslations("useMakeOffer")

  const items = itemKeys
    ? itemKeys.map(itemKey => ({
        ...readFragment(useMakeOfferFragment, itemKey),
        quantity: itemKey.quantity ?? 1,
      }))
    : []

  const makeOfferFlow = useCreateOfferFlow()
  const chainArch = useChainArch()

  const getDisabledReason = () => {
    if (items.length === 0) {
      return t("errors.noItems")
    }

    // Check if all items are from the same chain
    const allSameChain = items.every(
      item => item.chain.identifier === items[0].chain.identifier,
    )
    if (!allSameChain) {
      return t("errors.differentChains")
    }

    // Check chain architecture
    const firstItem = items[0]
    if (firstItem && firstItem.chain.arch !== chainArch) {
      return t("errors.differentChainArch")
    }

    // Check if any item is not tradable
    const hasUntradableItem = items.some(item => !isItemTradable(item))
    if (hasUntradableItem) {
      return t("errors.tradingDisabled")
    }

    return undefined
  }

  const disabledReason = getDisabledReason()
  const disabled = Boolean(disabledReason)

  function makeOffer() {
    if (items.length > 0) {
      makeOfferFlow(
        items.map(item => ({
          chain: { identifier: item.chain.identifier },
          contractAddress: item.contractAddress,
          tokenId: item.tokenId,
          quantity: item.quantity,
        })),
      )
    }
  }

  return {
    disabled,
    disabledReason,
    makeOffer,
  }
}
